import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { scoreColor } from './uavsMeta';
import { useTokens } from '../primitives';

const W = 560;
const H = 120;
const PAD_X = 28;
const PAD_Y = 18;

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

// Place each event at the x-coordinate of the scan point closest in time to
// occurredAt. This is more visually honest than interpolating — events live
// in the same scan as the point that captured them.
function attachEventsToPoints(points, events) {
  if (!points || !events?.length) return [];
  return events
    .map((e) => {
      const t = new Date(e.occurredAt).getTime();
      let nearest = points[0];
      let best = Math.abs(new Date(nearest.snapshotAt).getTime() - t);
      for (const p of points) {
        const d = Math.abs(new Date(p.snapshotAt).getTime() - t);
        if (d < best) {
          best = d;
          nearest = p;
        }
      }
      return { event: e, x: nearest.x, scanY: nearest.y };
    })
    .filter(Boolean);
}

export default function AnnotatedTrendline({ history, events }) {
  const t = useTokens();
  const { points, path } = useMemo(() => {
    const series = [...(history || [])].reverse();
    if (series.length < 2) return { points: null, path: '' };
    const pts = series.map((h, i) => {
      const score = h.score ?? 0;
      const x = PAD_X + (i * (W - PAD_X * 2)) / (series.length - 1);
      const y = H - PAD_Y - (score / 100) * (H - PAD_Y * 2);
      return { x, y, score, snapshotAt: h.snapshotAt || h.createdAt };
    });
    const d = pts
      .map(
        (p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
      )
      .join(' ');
    return { points: pts, path: d };
  }, [history]);

  const annotated = useMemo(
    () => attachEventsToPoints(points, events),
    [points, events]
  );

  if (!points) return null;

  const last = points[points.length - 1];
  const first = points[0];
  const delta = last.score - first.score;
  const color = scoreColor(last.score, t);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: W,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            Score trend · last {points.length} scans
          </Typography>
          <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
            {delta > 0 ? `+${delta}` : delta} points since{' '}
            {formatDate(first.snapshotAt)}
          </Typography>
        </Box>
        <svg
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          {/* Y-axis reference lines at 40 and 70 — the band thresholds in scoreColor */}
          {[40, 70].map((band) => {
            const y = H - PAD_Y - (band / 100) * (H - PAD_Y * 2);
            return (
              <line
                key={band}
                x1={PAD_X}
                x2={W - PAD_X}
                y1={y}
                y2={y}
                stroke={t.hairline}
                strokeDasharray="3 4"
                strokeWidth={1}
              />
            );
          })}
          {/* Event markers — drawn behind the line so the line stays readable */}
          {annotated.map((a, i) => (
            <line
              key={`mark-${i}`}
              x1={a.x}
              x2={a.x}
              y1={PAD_Y / 2}
              y2={H - PAD_Y / 2}
              stroke={t.cardBorder}
              strokeWidth={1}
            />
          ))}
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map((p, i) => (
            <circle
              key={`pt-${i}`}
              cx={p.x}
              cy={p.y}
              r={i === points.length - 1 ? 4 : 2.5}
              fill={color}
            />
          ))}
          {/* Event dots on the top edge — one per event, easier to read than scattered on the line */}
          {annotated.map((a, i) => (
            <circle
              key={`ev-${i}`}
              cx={a.x}
              cy={PAD_Y / 2}
              r={3}
              fill={t.muted}
            >
              <title>
                {formatDate(a.event.occurredAt)} — {a.event.title}
              </title>
            </circle>
          ))}
        </svg>
        {annotated.length > 0 && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
          >
            {annotated.length} {annotated.length === 1 ? 'change' : 'changes'}{' '}
            tracked across this window — full list below
          </Typography>
        )}
      </Box>
    </Box>
  );
}
